import { MouseEvent, useCallback, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import useMakeCall from "components/suno-dialer/call-patient/use-make-call.hook";
import { messageNotificationPatientId } from "features/inbox/text-routing/routing-reason.util";
import usePracticeVoiceEnabled from "hooks/use-practice-voice-enabled.hook";
import { useLazyGetPatientQuery } from "services/patients";
import {
  MessageNotification,
  SmsNotDeliveredExtra
} from "services/types/notifications";
import { isNotBlank } from "utils/common";

import MessageNotificationLink from "./message-notification-link.component";

// What a call needs: a number to dial and a name for the softphone's card.
const CALL_PATIENT_QUERY =
  "{id,first_name,last_name,phone,secondary_phone,preferred_clinic{id}}";

export const PHONE_TYPE_UPDATED_SUFFIX = "phone type set to Landline";

/** "Landline, can't receive texts · phone type set to Landline" */
export function smsNotDeliveredReasonLine(
  extra: Pick<SmsNotDeliveredExtra, "reason_label" | "phone_type_updated">
): string {
  return [
    extra.reason_label?.trim(),
    extra.phone_type_updated ? PHONE_TYPE_UPDATED_SUFFIX : null
  ]
    .filter(Boolean)
    .join(" · ");
}

// "Call patient" through the one dial path (never a tel: link). The
// notification carries no number, so the patient is fetched on the click.
// Hidden without the practice's Voice, like every other click-to-call surface.
function CallPatientAction({ patientId }: { patientId: number }) {
  const isPracticeVoiceEnabled = usePracticeVoiceEnabled();
  const makeCall = useMakeCall();
  const dispatch = useAppDispatch();
  const [fetchPatient] = useLazyGetPatientQuery();
  const [isDialing, setIsDialing] = useState(false);

  const handleClick = useCallback(
    // The click still reaches the row, which marks the notification read.
    async (event: MouseEvent<HTMLButtonElement>) => {
      const { altKey } = event;
      setIsDialing(true);
      try {
        const patient = await fetchPatient(
          { patientId, query: CALL_PATIENT_QUERY },
          true
        ).unwrap();
        const phone = [patient.phone, patient.secondary_phone]
          .filter(isNotBlank)
          .at(0);
        if (!phone) {
          dispatch(
            setCustomMessage("This patient has no phone number on file.")
          );
          dispatch(setSnackbarOpen(true));
          return;
        }
        makeCall(phone, patient, { altKey });
      } catch {
        dispatch(setCustomMessage("Could not load the patient to call."));
        dispatch(setSnackbarOpen(true));
      } finally {
        setIsDialing(false);
      }
    },
    [dispatch, fetchPatient, makeCall, patientId]
  );

  if (!isPracticeVoiceEnabled) return null;

  return (
    <Button
      size="small"
      variant="text"
      onClick={handleClick}
      disabled={isDialing}
      sx={{ p: 0, minWidth: 0 }}
    >
      Call patient
    </Button>
  );
}

/**
 * A MESSAGE bell row for a staff-typed text that was not delivered
 * (`extra.kind === "sms_not_delivered"`, Intercom 215476575290017):
 *
 *  Your text to Margaret Ellis was not delivered
 *  Landline, can't receive texts · phone type set to Landline
 *  Hi Margaret, your hearing aids have arrived. Call us…
 *  Open conversation   Fix number   Call patient
 */
const SmsNotDeliveredNotification = ({
  title,
  description,
  extra
}: Omit<MessageNotification, "extra"> & { extra: SmsNotDeliveredExtra }) => {
  const patientId = messageNotificationPatientId(extra);
  const conversationPath = `/inbox/${patientId ?? ""}`;
  const reasonLine = smsNotDeliveredReasonLine(extra);

  return (
    <ListItemText
      primary={
        <MessageNotificationLink
          to={conversationPath}
          style={{ display: "flex", flex: 1 }}
        >
          {title}
        </MessageNotificationLink>
      }
      secondary={
        <Stack component="span" gap={0.5}>
          {reasonLine ? (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              data-testid="sms-not-delivered-reason"
            >
              {reasonLine}
            </Typography>
          ) : null}
          {description ? (
            <Typography component="span" variant="body1">
              {description}
            </Typography>
          ) : null}
          {patientId !== null ? (
            <Stack component="span" direction="row" gap={2} alignItems="center">
              <Link
                component={RouterLink}
                to={conversationPath}
                underline="hover"
              >
                Open conversation
              </Link>
              <Link
                component={RouterLink}
                to={`/patients/${patientId}/profile`}
                underline="hover"
              >
                Fix number
              </Link>
              <CallPatientAction patientId={patientId} />
            </Stack>
          ) : null}
        </Stack>
      }
    />
  );
};

export default SmsNotDeliveredNotification;
